import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, UploadCloud, Download, Trash2, Loader2, Users, Paintbrush, Coins, Scale } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 focus:outline-none focus:ring-1 focus:ring-nc-orange";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";
const card = "border border-white/10 bg-black/40 p-5 flex flex-col gap-4";

/* Blueprints — publish this server's whole deck setup (identity, widgets, economy,
   ruleset, faction definitions) to the community library; import someone else's
   onto the selected server in one click. Entry-exam answers and faction rosters
   never transfer. */
export function BlueprintPanel({ sid, me, onApplied }) {
  const [blueprints, setBlueprints] = useState([]);
  const [form, setForm] = useState({ name: "", description: "" });
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState("");

  const load = useCallback(() => {
    api.get("/nc/blueprints").then((r) => setBlueprints(r.data.blueprints)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const publish = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/nc/servers/${sid}/blueprint/publish`, form);
      toast.success(r.data.message);
      setForm({ name: "", description: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const doImport = async (b) => {
    setBusyId(b.id);
    try {
      const r = await api.post(`/nc/servers/${sid}/blueprint/import`, { blueprint_id: b.id });
      toast.success(r.data.message);
      load();
      onApplied?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusyId(""); }
  };

  const remove = async (b) => {
    try {
      const r = await api.delete(`/nc/blueprints/${b.id}`);
      toast.success(r.data.message);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const contents = (b) => {
    const p = b.payload || {};
    const bits = [];
    if (p.theme && Object.keys(p.theme).length) bits.push("identity");
    if ((p.theme?.widgets || []).length) bits.push(`${p.theme.widgets.length} widgets`);
    if (p.economy && Object.keys(p.economy).length) bits.push("economy");
    if (p.ruleset && Object.keys(p.ruleset).length) bits.push("ruleset");
    if ((p.factions || []).length) bits.push(`${p.factions.length} factions`);
    return bits.join(" · ") || "empty";
  };

  return (
    <div className="flex flex-col gap-5">
      {/* publish */}
      <div className={card}>
        <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2"><UploadCloud className="w-3.5 h-3.5" /> Publish this server's setup</p>
        <p className="font-mono text-[9px] text-zinc-500 leading-relaxed">Snapshots your identity & loading screen, widgets, economy, ruleset and faction definitions into a one-click bundle for the community. Entry-exam questions and faction rosters are never shared.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><span className={labelCls}>Blueprint name</span><input data-testid="bp-name" className={inputCls} maxLength={60} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Hardcore City Starter" /></div>
          <div><span className={labelCls}>Description</span><input data-testid="bp-desc" className={inputCls} maxLength={300} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Serious RP economy + full BPD ranks" /></div>
        </div>
        <button data-testid="bp-publish" disabled={busy || form.name.trim().length < 3} onClick={publish} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-mono text-[10px] tracking-[0.2em] uppercase px-5 py-3 transition-colors w-fit flex items-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />} Publish blueprint
        </button>
      </div>

      {/* library */}
      <div className={card}>
        <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2"><Package className="w-3.5 h-3.5" /> Community library</p>
        {blueprints.length === 0 && <p className="font-mono text-[10px] text-zinc-600">No blueprints yet — publish the first one above.</p>}
        {blueprints.map((b) => (
          <div key={b.id} data-testid={`bp-row-${b.id.slice(0, 8)}`} className="border border-white/10 bg-[#070707] px-4 py-3.5 flex items-center gap-3 flex-wrap">
            <Package className="w-4 h-4 text-nc-orange shrink-0" />
            <div className="min-w-0">
              <p className="font-sans font-semibold text-sm text-zinc-100 flex items-center gap-2 flex-wrap">
                {b.name}
                <span className="font-mono text-[8px] uppercase text-zinc-500">by {b.author}</span>
              </p>
              <p className="font-mono text-[9px] text-zinc-500 flex items-center gap-2 flex-wrap mt-0.5">
                {b.description && <span className="text-zinc-400">{b.description} —</span>}
                <span className="flex items-center gap-1"><Paintbrush className="w-2.5 h-2.5" /><Coins className="w-2.5 h-2.5" /><Scale className="w-2.5 h-2.5" />{contents(b)}</span>
                <span className="flex items-center gap-1"><Users className="w-2.5 h-2.5" />{b.imports} imports</span>
              </p>
            </div>
            <div className="ml-auto flex gap-1.5">
              <button data-testid={`bp-import-${b.id.slice(0, 8)}`} disabled={busyId === b.id} onClick={() => doImport(b)} className="font-mono text-[8px] uppercase border border-nc-green/40 text-nc-green hover:bg-nc-green/10 px-2.5 py-1.5 transition-colors disabled:opacity-40 flex items-center gap-1">
                {busyId === b.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Import here
              </button>
              {(me?.role === "admin" || b.owner_id === me?.id || b.server_id === sid) && (
                <button data-testid={`bp-del-${b.id.slice(0, 8)}`} onClick={() => remove(b)} className="border border-red-500/40 p-1.5 text-red-400 hover:bg-red-500/10 transition-colors"><Trash2 className="w-3 h-3" /></button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
